"use client"

import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component"
import "react-vertical-timeline-component/style.min.css"
import { FaTimes } from "react-icons/fa"
import { modalVariants } from "../../../auth/signup/motion"
import { motion, AnimatePresence } from "framer-motion"

const MainApplication = () => {
  const { id } = useParams()
  const [activities, setActivities] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [newActivity, setNewActivity] = useState("")
  const [successMessage, setSuccessMessage] = useState(null)

  // ⭐ ADD STATUS MAP HERE
  const statusMap = {
    WithdrawnByAgent: "Withdrawn",
    WithdrawnByClient: "Cancelled",
    VisaApplied: "Visa Applied",
  }

  // ⭐ Helper to extract status dynamically
  const getReadableStatus = (action) => {
    if (!action) return action

    // action example: "Haris Khan has updated status to WithdrawnByAgent"
    const parts = action.split(" ")
    const lastWord = parts[parts.length - 1] // WithdrawnByAgent

    const readable = statusMap[lastWord] || lastWord

    // Return full sentence with name preserved
    return action.replace(lastWord, readable)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchActivities()
  }, [id])

  const fetchActivities = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const token = localStorage.getItem("token")

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/getActivityLogs/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch activities")
      }

      const data = await response.json()
      console.log(data)
      setActivities(data.body)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitActivity = async () => {
    if (!newActivity.trim()) return

    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/createActivityLog`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          applicationId: id,
          reason: newActivity,
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to add activity")
      }

      const data = await response.json()
      console.log("Activity created:", data)

      // Show success message
      setSuccessMessage({
        message: data.message || "Activity log created successfully",
        details: data.body,
        timestamp: new Date().toLocaleString(),
      })

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 5000)

      // Close modal and clear input
      setShowModal(false)
      setNewActivity("")

      // Refresh activities to show the new one
      await fetchActivities()
    } catch (err) {
      console.error("Error adding activity:", err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-4">
        <div className="text-red-500 text-lg mb-4">Error: {error}</div>
        <button onClick={fetchActivities} className="px-4 py-2 bg-[#0c4a6e] text-white rounded-lg transition-colors">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8 relative">
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold">{successMessage.message}</h3>
            <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-800">
              <FaTimes />
            </button>
          </div>
          {successMessage.details && (
            <div className="bg-white p-3 rounded border border-green-100 text-sm">
              <p>
                <span className="font-medium">Action:</span> {successMessage.details.action}
              </p>
              <p>
                <span className="font-medium">Reason:</span> {successMessage.details.reason}
              </p>
              <p>
                <span className="font-medium">ID:</span> {successMessage.details._id}
              </p>
              <p className="text-green-600 text-xs mt-1">
                {new Date(successMessage.details.timestamp).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowModal(true)}
          className="px-8 py-2 bg-[#005787] text-white rounded-full transition-colors"
        >
          Add
        </button>
      </div>

      {showModal && (
        <AnimatePresence>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center -top-20 z-50"
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div className="bg-white rounded-lg p-6 w-full max-w-md relative" variants={modalVariants}>
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
              <h3 className="text-lg font-semibold mb-4">Add Activity</h3>
              <textarea
                value={newActivity}
                onChange={(e) => setNewActivity(e.target.value)}
                placeholder="Enter new activity of this application"
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 min-h-[100px]"
              />
              <div className="flex justify-center">
                <motion.button
                  onClick={handleSubmitActivity}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-4 py-2 bg-[#005787] text-white rounded-full transition-colors"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Submitting...
                    </span>
                  ) : (
                    "Submit"
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}

      <VerticalTimeline layout="1-column-left" lineColor="#005787">
        {activities.length > 0 ? (
          <>
            {activities?.map((activity, index) => (
              <VerticalTimelineElement
                key={index}
                iconStyle={{ background: "#005787", color: "#fff" }}
                contentStyle={{
                  boxShadow: "none",
                  WebkitBoxShadow: "none",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  padding: "1.5rem",
                  background: "#fff",
                }}
                contentArrowStyle={{ display: "none" }}
              >
                <div className="mb-2 font-semibold">{activity._id}</div>
                <div className="space-y-2">
                  {activity.logs.map((log, logIndex) => (
                    <div key={logIndex} className="bg-gray-50 p-3 border rounded-lg">
                      <p style={{ fontSize: "14px" }} className="text-gray-800">
                        {getReadableStatus(log.action)}
                      </p>

                      <p
                        style={{
                          fontSize: "14px",
                        }}
                        className="text-gray-500 font-normal"
                      >{`${log?.reason ? `Reason: ${log.reason} ` : ""}`}</p>
                      <p
                        style={{
                          color: "#0c4a6e",
                          fontStyle: "italic",
                          fontWeight: "300",
                          fontSize: "12px",
                        }}
                      >
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </VerticalTimelineElement>
            ))}
          </>
        ) : (
          <div className="flex flex-col justify-center items-center">
            <p className="text-red-500 p-6 text-center text-sm">No application logs available.</p>
            <span
              onClick={() => navigate("/agent/application-log")}
              className="cursor-pointer text-white w-max bg-[#0c4a6e] rounded-full text-sm py-2 px-5"
            >
              Back to Application log
            </span>
          </div>
        )}
      </VerticalTimeline>
    </div>
  )
}

export default MainApplication
